import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormikContext, getIn } from 'formik';
import clsx from 'clsx';
import { Dropdown, LabelComponent, Stack, Text, Tooltip } from 'components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getZCodes } from 'store/clientProfile/clientProfileActions';
import { clearZCodes } from 'store/clientProfile/clientProfileSlice';
import { ErrorContainer } from 'components';
import debounce from 'lodash.debounce';

import ZCodesSelect from '../../zCodesSelect/zCodesSelect';
import style from './typeOfSupport.module.scss';
import Branch from '../../components/branch';
import branchStyle from '../../components/branch.module.scss';

interface ZCodesFieldProps {
  disabled: boolean;
}

export const ZCodesField = ({ disabled }: ZCodesFieldProps) => {
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);
  const formik = useFormikContext<{
    zCodes: {
      id: number;
      name: string;
      explanations: { id: number; explanation: string }[];
      selectedExplanations: number[];
    }[];
  }>();
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const { setFieldValue } = formik;
  const [searchInputValue, setSearchInputValue] = useState('');
  const favoriteZCodes = useAppSelector((state) => state.clientProfile.favoriteZCodes.data);
  const zCodes = useAppSelector((state) => state.clientProfile.zCodes.data);
  const pendingGetZCodesRef = useRef<{ abort: () => void } | null>(null);

  const mappedFavoriteZCodes = favoriteZCodes.map((z) => ({
    id: z.id,
    name: `${z.code} - ${z.description}`,
    explanations: z.explanations
  }));

  const mappedZCodes = zCodes.map((z) => ({
    id: z.id,
    name: `${z.code} - ${z.description}`,
    explanations: z.explanations
  }));

  const data = searchInputValue ? mappedZCodes : mappedFavoriteZCodes;

  const debouncedSearch = useCallback(
    debounce(
      (
        query: string,
        _pendingGetZCodesRef: React.MutableRefObject<{ abort: () => void } | null>
      ) => {
        if (query) {
          const promise = dispatch(getZCodes({ query }));
          _pendingGetZCodesRef.current = promise;
        }
      },
      200
    ),
    [clientId, dispatch]
  );

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    pendingGetZCodesRef.current?.abort();
    if (value === '') {
      dispatch(clearZCodes());
    } else {
      debouncedSearch(value, pendingGetZCodesRef);
    }
  };

  return (
    <>
      <Branch
        label={
          <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
            <LabelComponent
              text="Select SDOH Z codes"
              mandatory={isBillingEnabled}
              className={branchStyle.branch}
            />
            <Tooltip
              placement="right"
              toolTipText={
                <Stack
                  width="333px"
                  display="flex"
                  gap="8px"
                  padding="4px"
                  className={style.typeOfSupportInfo}
                >
                  <p>
                    These codes help track the social and environmental factors that impact a
                    client's health. Please select the codes that were most relevant to this
                    encounter.
                  </p>
                </Stack>
              }
            >
              <span className={clsx('material-icons', style.infoIcon)}>info</span>
            </Tooltip>
          </Stack>
        }
      >
        <ZCodesSelect
          showTitle={searchInputValue !== '' ? false : true}
          data={data.map((z) => ({ id: z.id, name: z.name, info: z.name }))}
          value={formik.values.zCodes.map((z) => ({
            id: z.id.toString(),
            name: z.name
          }))}
          onClose={() => {
            setSearchInputValue('');
            dispatch(clearZCodes());
          }}
          name="zCodes"
          onBlur={formik.handleBlur}
          searchInputValue={searchInputValue}
          onSearchInputChange={(e) => onSearchInputChange(e)}
          onChange={(e) => {
            if (Array.isArray(e.target.value)) {
              const removedFromCurrentValues = formik.values.zCodes.filter(
                (zCode) => !e.target.value.includes(zCode.id.toString())
              );

              if (removedFromCurrentValues.length > 0) {
                setFieldValue(
                  'zCodes',
                  formik.values.zCodes.filter(
                    (z) => !removedFromCurrentValues.some((zCode) => zCode.id === z.id)
                  )
                );
                return;
              }

              const notInCurrentValues = e.target.value.filter(
                (zCode) => !formik.values.zCodes.find((z) => z.id.toString() === zCode)
              );

              const found = data.find((zCode) => zCode.id.toString() === notInCurrentValues[0]);

              console.log(found);

              if (found) {
                setFieldValue('zCodes', [...formik.values.zCodes, found]);
              }
            }
          }}
          disabled={disabled}
          withGradient
          label="Select"
        />

        <ErrorContainer
          name="zCodes"
          errors={formik.errors}
          visible={
            formik.touched.zCodes &&
            Boolean(formik.errors.zCodes) &&
            typeof formik.errors.zCodes === 'string'
          }
        />
        {formik.values.zCodes.length > 0 && (
          <>
            <Stack className={branchStyle.verticalBranch} ml="10px" pl="26px" pb="4px">
              <LabelComponent text="Explain SDOH Z codes" mandatory />
            </Stack>
            <Stack mb="16px">
              {formik.values.zCodes.map((z, index) => (
                <Branch
                  key={z.id}
                  label={
                    <Stack mb="4px">
                      <Text size="normal" weight="medium">
                        {z.name}*
                      </Text>
                    </Stack>
                  }
                >
                  <Stack pb={index === formik.values.zCodes.length - 1 ? '0' : '16px'}>
                    <Dropdown
                      data={z.explanations?.map((e) => ({ id: e.id, name: e.explanation })) ?? []}
                      value={z.selectedExplanations?.map((e) => e.toString()) ?? []}
                      onChange={(e) => {
                        formik.setFieldValue(
                          `zCodes[${index}].selectedExplanations`,
                          e.target.value
                        );
                      }}
                      onBlur={formik.handleBlur}
                      multiSelect
                      name={`zCodes[${index}].selectedExplanations`}
                      disabled={disabled}
                      withGradient
                      label="Select"
                    />
                    {getIn(formik.errors, `zCodes[${index}].selectedExplanations`) && (
                      <span className={style.error}>
                        {getIn(formik.errors, `zCodes[${index}].selectedExplanations`)}
                      </span>
                    )}
                  </Stack>
                </Branch>
              ))}
            </Stack>
          </>
        )}
      </Branch>
    </>
  );
};
